
import { useAuth } from '@/hooks/useAuth';
import { isAdmin, isStudent, isTutor } from '@/utils/roleValidation';
import { useTutorStatus } from '@/hooks/useTutorStatus';
import { Navigate, useLocation } from 'react-router-dom';

interface ProtectedTutorRouteProps {
  children: React.ReactNode;
}

const ProtectedTutorRoute = ({ children }: ProtectedTutorRouteProps) => {
  const { user, isLoading } = useAuth();
  const location = useLocation();
  const { hasAccessToTutorFeatures, isPendingTutor, isSuspendedTutor, isLoading: statusLoading } = useTutorStatus();

  // Show loading while checking authentication and tutor status
  if (isLoading || statusLoading) {
    return (
      <div className="min-h-screen w-full flex items-center justify-center bg-background">
        <div className="flex flex-col items-center justify-center gap-4 px-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          <p className="text-muted-foreground text-center">Loading...</p>
        </div>
      </div>
    );
  }

  // Redirect to auth if not logged in
  if (!user) {
    // Store the intended destination for redirect after login
    const fullPath = location.pathname + location.search + location.hash;
    sessionStorage.setItem('redirectAfterLogin', fullPath);
    return <Navigate to="/auth" replace />;
  }

  // Allow access for admins and students
  if (isAdmin(user) || isStudent(user)) {
    return <>{children}</>;
  }

  // For tutors, check if they have access to tutor features
  if (isTutor(user) && !hasAccessToTutorFeatures()) {
    // Redirect pending or suspended tutors to dashboard with limited access
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};

export default ProtectedTutorRoute;
